import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  const next = useCallback(() => setActive((value) => (value + 1) % total), [total]);
  const prev = useCallback(() => setActive((value) => (value - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(next, 5500);
    return () => window.clearInterval(timer);
  }, [next, paused]);

  const slide = heroSlides[active];

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden bg-background-100 md:h-[600px] lg:h-[660px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点影视推荐"
    >
      {heroSlides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img src={item.image} alt={`${item.title} 剧照`} className="h-full w-full object-cover object-top" />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/40 to-transparent" />

      <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-10 md:px-6 lg:px-10 lg:pb-14">
        <div key={slide.id} className="max-w-2xl animate-fadeUp">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
            {slide.tag}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-5xl lg:text-6xl">
            {slide.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-700">
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill" />
              {slide.rating.toFixed(1)}
            </span>
            <span className="hidden sm:inline">{slide.meta}</span>
          </div>
          <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
            {slide.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/movies"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50/60 px-6 py-3 text-sm font-label text-foreground-800 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
            >
              <i className="ri-film-line text-lg" />
              浏览电影
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {heroSlides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到 ${item.title}`}
                aria-current={index === active}
                className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                  index === active ? "w-8 bg-primary-500" : "w-4 bg-background-300 hover:bg-primary-400/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={prev}
              aria-label="上一张"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 bg-background-50/60 text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="下一张"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 bg-background-50/60 text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}